/**
 * Televisores Cheesy: encendido, pausa fuera de pantalla y cambio de foto.
 *
 * El encendido es CSS y corre al cargar la página (assets/chz-base.css, "Televisor prendido").
 * Este script solo lo posterga para los televisores que todavía no se ven: les pone chz-tv--wait
 * y se la saca la primera vez que entran a la pantalla, así cada tele se prende cuando aparece.
 * Fuera de pantalla, la franja que recorre la imagen y el parpadeo se pausan (is-offscreen).
 *
 * Los botones [data-chz-tv-thumb] cambian la foto del televisor de su grupo [data-chz-tv-group]
 * con la estática del cambio de canal (data-src, data-srcset, data-alt y data-focal).
 * Las perillas de la carcasa [data-chz-tv-knob] pasan a la foto siguiente o anterior del grupo; la
 * pantalla muestra un momento qué foto es (2/4). Sin más fotos, solo hacen la estática.
 *
 * La lupa [data-chz-tv-zoom] (toda la pantalla es el botón) abre la foto que se está viendo entera, sin
 * recortar, en un diálogo con flechas entre las fotos del grupo (data-zoom de cada foto). Se cierra con la
 * X, Escape o tocando fuera de la foto; las flechas del teclado y deslizar el dedo también pasan de foto.
 * Mientras está abierto, la tele no pasa fotos sola.
 */
/**
 * Prende una tele que esperaba: cuando su foto ya cargó (o falló), o a los 4 s como máximo.
 * @param {HTMLElement} tv
 */
function powerOn(tv) {
  if (tv.dataset.chzTvPowering !== undefined) return;
  tv.dataset.chzTvPowering = '';
  const go = () => tv.classList.remove('chz-tv--wait');
  const photo = tv.querySelector('.chz-tv__photo, .chz-tv__screen > img, .chz-tv__link > img');
  if (!(photo instanceof HTMLImageElement) || photo.complete) {
    go();
    return;
  }
  photo.addEventListener('load', go, { once: true });
  photo.addEventListener('error', go, { once: true });
  window.setTimeout(go, 4000);
}

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const tv = entry.target;
          if (!(tv instanceof HTMLElement)) continue;
          tv.classList.toggle('is-offscreen', !entry.isIntersecting);
          if (entry.isIntersecting && tv.classList.contains('chz-tv--wait')) powerOn(tv);
        }
      })
    : null;

/** @param {ParentNode} root */
function watch(root) {
  if (!observer) return;
  const height = window.innerHeight;
  for (const tv of root.querySelectorAll('.chz-tv')) {
    if (!(tv instanceof HTMLElement) || tv.dataset.chzTvWatched !== undefined) continue;
    tv.dataset.chzTvWatched = '';
    const box = tv.getBoundingClientRect();
    if (box.top >= height || box.bottom <= 0) tv.classList.add('chz-tv--wait');
    observer.observe(tv);
  }
}

/** @param {MouseEvent} event */
function onThumbClick(event) {
  const thumb = event.target instanceof Element ? event.target.closest('[data-chz-tv-thumb]') : null;
  if (!(thumb instanceof HTMLElement) || thumb.getAttribute('aria-pressed') === 'true') return;

  const group = thumb.closest('[data-chz-tv-group]');
  const photo = group?.querySelector('.chz-tv__photo');
  const screen = group?.querySelector('.chz-tv__screen');
  if (!(photo instanceof HTMLImageElement) || !(screen instanceof HTMLElement)) return;

  for (const other of group.querySelectorAll('[data-chz-tv-thumb]')) {
    other.setAttribute('aria-pressed', String(other === thumb));
  }

  if (thumb.dataset.srcset) photo.srcset = thumb.dataset.srcset;
  if (thumb.dataset.src) photo.src = thumb.dataset.src;
  photo.alt = thumb.dataset.alt ?? '';
  photo.style.objectPosition = thumb.dataset.focal ?? '';

  screen.classList.remove('is-switching');
  void screen.offsetWidth;
  screen.classList.add('is-switching');
  window.setTimeout(() => screen.classList.remove('is-switching'), 260);
}

/** @param {Element} screen */
function flashStatic(screen) {
  screen.classList.remove('is-switching');
  void (/** @type {HTMLElement} */ (screen)).offsetWidth;
  screen.classList.add('is-switching');
  window.setTimeout(() => screen.classList.remove('is-switching'), 260);
}

/** @param {MouseEvent} event */
function onKnobClick(event) {
  const knob = event.target instanceof Element ? event.target.closest('[data-chz-tv-knob]') : null;
  if (!(knob instanceof HTMLElement)) return;
  const tv = knob.closest('.chz-tv');
  if (!(tv instanceof HTMLElement)) return;

  tv.classList.remove('is-knobbed');
  void tv.offsetWidth;
  tv.classList.add('is-knobbed');
  window.setTimeout(() => tv.classList.remove('is-knobbed'), 320);

  const group = tv.closest('[data-chz-tv-group]');
  const thumbs = group ? [...group.querySelectorAll('[data-chz-tv-thumb]')] : [];
  const screen = tv.querySelector('.chz-tv__screen');
  if (thumbs.length < 2) {
    if (screen) flashStatic(screen);
    return;
  }

  const current = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true'));
  const step = knob.dataset.chzTvKnob === 'prev' ? -1 : 1;
  const next = (current + step + thumbs.length) % thumbs.length;
  const target = thumbs[next];
  if (target instanceof HTMLElement) target.click();

  const osd = tv.querySelector('.chz-tv__osd');
  if (osd instanceof HTMLElement) {
    osd.dataset.chzLabel ??= osd.textContent ?? '';
    osd.textContent = `${osd.dataset.chzLabel} · ${next + 1}/${thumbs.length}`;
    window.clearTimeout(Number(osd.dataset.chzTimer));
    osd.dataset.chzTimer = String(window.setTimeout(() => (osd.textContent = osd.dataset.chzLabel ?? ''), 1600));
  }
}

/* ---------- Fotos que pasan solas ----------
   Los grupos con data-chz-tv-auto (Juego destacado) pasan a la foto siguiente cada AUTO_MS mientras la
   tele se ve. No avanzan con el puntero o el foco encima, durante PAUSE_MS después de que la persona toque
   una foto o una perilla, con la pestaña oculta, con movimiento reducido o con las animaciones pausadas.
   Cada tele arranca desfasada para que no cambien todas a la vez. */
const AUTO_MS = 4500;
const PAUSE_MS = 15000;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
/** @type {WeakMap<Element, number>} */
const nextAt = new WeakMap();
let autoIndex = 0;

/** @param {MouseEvent} event */
function onUserTouch(event) {
  if (!event.isTrusted || !(event.target instanceof Element)) return;
  const control = event.target.closest('[data-chz-tv-thumb], [data-chz-tv-knob]');
  const group = control?.closest('[data-chz-tv-group]');
  if (group) nextAt.set(group, Date.now() + PAUSE_MS);
}

function tickAuto() {
  if (document.hidden || reducedMotion.matches || document.documentElement.dataset.chzMotion === 'off') return;
  if (zoomBox?.dialog.open) return;
  const now = Date.now();
  for (const group of document.querySelectorAll('[data-chz-tv-group][data-chz-tv-auto]')) {
    const thumbs = [...group.querySelectorAll('[data-chz-tv-thumb]')];
    if (thumbs.length < 2) continue;
    if (!nextAt.has(group)) {
      nextAt.set(group, now + AUTO_MS + autoIndex * 1700);
      autoIndex += 1;
      continue;
    }
    const tv = group.querySelector('.chz-tv');
    const busy = group.matches(':hover') || group.matches(':focus-within');
    const hidden = !tv || tv.classList.contains('is-offscreen') || tv.classList.contains('chz-tv--wait');
    if (busy || hidden) {
      nextAt.set(group, Math.max(nextAt.get(group) ?? 0, now + 1500));
      continue;
    }
    if (now < (nextAt.get(group) ?? 0)) continue;
    const current = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true'));
    const target = thumbs[(current + 1) % thumbs.length];
    if (target instanceof HTMLElement) target.click();
    nextAt.set(group, now + AUTO_MS);
  }
}

/* ---------- Foto en grande ---------- */
/** @type {{ dialog: HTMLDialogElement, img: HTMLImageElement, count: HTMLElement, prev: HTMLButtonElement, next: HTMLButtonElement } | null} */
let zoomBox = null;
/** @type {{ group: HTMLElement, items: { src: string, alt: string }[], index: number, trigger: HTMLElement | null, countLabel: string } | null} */
let zoomState = null;

/** @param {string[]} labels */
function buildZoom(labels) {
  const [close, prev, next] = labels;
  const dialog = document.createElement('dialog');
  dialog.className = 'chz-zoom';
  dialog.innerHTML = `
    <figure class="chz-zoom__figure"><img class="chz-zoom__img" alt="" decoding="async"></figure>
    <p class="chz-zoom__count" aria-live="polite"></p>
    <button type="button" class="chz-zoom__btn chz-zoom__close" data-zoom-close></button>
    <button type="button" class="chz-zoom__btn chz-zoom__prev" data-zoom-step="-1"></button>
    <button type="button" class="chz-zoom__btn chz-zoom__next" data-zoom-step="1"></button>`;
  const set = (/** @type {string} */ selector, /** @type {string} */ label, /** @type {string} */ icon) => {
    const button = dialog.querySelector(selector);
    if (!button) return;
    button.setAttribute('aria-label', label);
    button.innerHTML = icon;
  };
  const svg = (/** @type {string} */ d) => `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  set('[data-zoom-close]', close, svg('M6 6l12 12M18 6L6 18'));
  set('.chz-zoom__prev', prev, svg('M15 5l-7 7 7 7'));
  set('.chz-zoom__next', next, svg('M9 5l7 7-7 7'));
  document.body.append(dialog);

  dialog.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-zoom-close]') || target === dialog || target.classList.contains('chz-zoom__figure')) {
      dialog.close();
      return;
    }
    const step = target.closest('[data-zoom-step]');
    if (step instanceof HTMLElement) showZoom(Number(step.dataset.zoomStep));
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') showZoom(-1);
    if (event.key === 'ArrowRight') showZoom(1);
  });
  let startX = 0;
  dialog.addEventListener('pointerdown', (event) => {
    startX = event.clientX;
  });
  dialog.addEventListener('pointerup', (event) => {
    const dx = event.clientX - startX;
    if (Math.abs(dx) > 60 && event.pointerType !== 'mouse') showZoom(dx < 0 ? 1 : -1);
  });
  dialog.addEventListener('close', () => {
    zoomState?.trigger?.focus({ preventScroll: true });
    if (zoomState) nextAt.set(zoomState.group, Date.now() + PAUSE_MS);
    zoomState = null;
  });

  return {
    dialog,
    img: /** @type {HTMLImageElement} */ (dialog.querySelector('.chz-zoom__img')),
    count: /** @type {HTMLElement} */ (dialog.querySelector('.chz-zoom__count')),
    prev: /** @type {HTMLButtonElement} */ (dialog.querySelector('.chz-zoom__prev')),
    next: /** @type {HTMLButtonElement} */ (dialog.querySelector('.chz-zoom__next')),
  };
}

/** Muestra la foto actual (o avanza step) y deja la tele en la misma foto. @param {number} step */
function showZoom(step) {
  if (!zoomBox || !zoomState) return;
  const total = zoomState.items.length;
  if (step && total > 1) {
    zoomState.index = (zoomState.index + step + total) % total;
    const thumbs = zoomState.group.querySelectorAll('[data-chz-tv-thumb]');
    const thumb = thumbs[zoomState.index];
    if (thumb instanceof HTMLElement && thumb.getAttribute('aria-pressed') !== 'true') thumb.click();
  }
  const item = zoomState.items[zoomState.index];
  zoomBox.img.src = item.src;
  zoomBox.img.alt = item.alt;
  const many = total > 1;
  zoomBox.prev.hidden = !many;
  zoomBox.next.hidden = !many;
  zoomBox.count.hidden = !many;
  zoomBox.count.textContent = zoomState.countLabel.replace('[n]', String(zoomState.index + 1)).replace('[total]', String(total));
}

/** @param {MouseEvent} event */
function openZoom(event) {
  const trigger = event.target instanceof Element ? event.target.closest('[data-chz-tv-zoom]') : null;
  const group = trigger?.closest('[data-chz-tv-group]');
  if (!(trigger instanceof HTMLElement) || !(group instanceof HTMLElement)) return;
  const labels = (group.dataset.zoomLabels || '').split('|');
  const thumbs = [...group.querySelectorAll('[data-chz-tv-thumb]')];
  const items = thumbs.length
    ? thumbs.map((thumb) => ({ src: thumb.getAttribute('data-zoom') || thumb.getAttribute('data-src') || '', alt: thumb.getAttribute('data-alt') || '' }))
    : [{ src: group.dataset.zoomSrc || '', alt: group.dataset.zoomAlt || '' }];
  if (!items.length || !items[0].src) return;
  zoomBox ??= buildZoom(labels);
  zoomBox.dialog.setAttribute('aria-label', labels[4] || '');
  const index = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true'));
  zoomState = { group, items, index, trigger, countLabel: labels[3] || '[n] / [total]' };
  showZoom(0);
  zoomBox.dialog.showModal();
}

watch(document);
document.addEventListener('click', onUserTouch, true);
document.addEventListener('click', onThumbClick);
document.addEventListener('click', onKnobClick);
document.addEventListener('click', openZoom);
window.setInterval(tickAuto, 500);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) watch(event.target);
});
